import { Injectable } from '@angular/core';
import {
  RatingApiProviderService,
  RequisitionApiProviderService,
  TimeSlotsApiProviderService,
} from '@project/data-providers';
import { UserProfileDataService } from '@project/services';
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, race, Subject, throwError } from 'rxjs';
import {
  IRequisition,
  IRequisitionReason,
  ITimeSlot,
  ERequisitionFilterType,
  IRequisitionAnswerDTO,
  DoctorSpecialization,
  IDoctor,
  DoctorRatingDTO,
  ReferralGuideSpecialty,
  ERequisitionStageStatusDTO,
  IRequisitionUpdateEvent,
  ERequisitionStageTypeDTO,
} from '@project/view-models';
import { catchError, filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { EPatientSessionAction, IRequisitionWithParticipantAndAppointment } from './consts';
import { DateFormatter, everyMinute$, isArraysContentEqual, TGuid } from '@core/helpers';
import { ApplicationRuntimeService, BroadcastEventsService } from '@core/services';
import { CloseAsidePageEvent, ActionsManagerService, STATIC_DATE_FORMAT } from '@project/shared';
import { RequisitionHelpersService, RequisitionIsUpdatedEventFeatureService } from '@project/services';
import { IModalComponentRef, ModalOverlayService } from '@lib/modal';
import { ReasonsComponent } from 'src/app/project/components/modal-reasons/modal-reasons.component';
import { ModalReasonsService } from 'src/app/project/components/modal-reasons/modal-reasons.service';
import { ModalDoctorRatingComponent } from './modal-doctor-rating/modal-doctor-rating.component';
import { ApiService } from '@core/http';
import { environment } from '@env';
import { Schedule } from './schedule.interface';
import { WebsocketAvailableProvider, WebsocketLifecycleService } from '@core/realtime/websocket';
import { VideoCallManagerService } from 'src/app/features/modules/video-call/video-call-manager.service';

@Injectable()
export class SessionsPatientManagerService extends ActionsManagerService<EPatientSessionAction> {
  protected readonly actionsHandlersMap = {
    [EPatientSessionAction.CloseRequisition]: (requisitionId: TGuid) => this.closeRequisition(requisitionId),
  };

  private readonly _requisitions$ = new BehaviorSubject<IRequisition[]>([]);
  private readonly _appointments$ = new BehaviorSubject<ITimeSlot[]>([]);
  public readonly appointments$ = this._appointments$.pipe();

  private modalRef: IModalComponentRef<ReasonsComponent>;
  private doctorRatingModalRef: IModalComponentRef<ModalDoctorRatingComponent>;

  public readonly allowDoctorRequest$: Observable<boolean> = this._requisitions$.pipe(
    map((requisitions) => requisitions.length === 0),
  );

  private readonly _appointmentsLiveIds$ = new BehaviorSubject<TGuid[]>([]);

  private readonly destroyed$ = new Subject();

  public readonly activeRequisitions$: Observable<IRequisitionWithParticipantAndAppointment[]> = combineLatest([
    this._requisitions$,
    this._appointments$,
    this._appointmentsLiveIds$,
  ]).pipe(
    map(([requisitions, appointments, liveAppointmentsIds]) => {
      const allAppointmentsRequisitionsIds: TGuid[] = appointments.map((item) => item.requisitionId);
      const liveAppointmentsRequisitionsIds: TGuid[] = appointments
        .filter((item) => liveAppointmentsIds.includes(item.id))
        .map((item) => item.requisitionId);

      // Requisition is live if it's appointment if live of it's not an appointment requisition at all
      return requisitions.filter(
        (item) =>
          liveAppointmentsRequisitionsIds.includes(item.id) || !allAppointmentsRequisitionsIds.includes(item.id),
      );
    }),
    map((requisitions) =>
      requisitions.map((req) => {
        const participant = this.requisitionHelpersService.getRequisitionParticipantForCurrentUser(req);
        const appointments = this._appointments$.value;

        let appointment: ITimeSlot | undefined;
        if (Array.isArray(appointments) && appointments.length > 0) {
          console.log('appointments', appointments);
          appointment = appointments?.find((a) => a.requisitionId === req.id);
        }

        return {
          ...req,
          participant,
          appointment,
        };
      }),
    ),
  );

  constructor(
    private requisitionApiProviderService: RequisitionApiProviderService,
    private userProfileDataService: UserProfileDataService,
    private timeSlotsApiProviderService: TimeSlotsApiProviderService,
    private notificationsService: NotificationsService,
    private requisitionIsUpdatedEventFeatureService: RequisitionIsUpdatedEventFeatureService,
    private requisitionHelpersService: RequisitionHelpersService,
    private broadcastEventsService: BroadcastEventsService,
    private applicationRuntimeService: ApplicationRuntimeService,
    private websocketLifecycleService: WebsocketLifecycleService,
    private modalOverlayService: ModalOverlayService,
    private modalReasonsService: ModalReasonsService,
    private ratingService: RatingApiProviderService,
    private videoCallManagerService: VideoCallManagerService,
    private apiService: ApiService,
  ) {
    super();
  }

  init() {
    merge(
      this.requisitionIsUpdatedEventFeatureService.requisitionClosed$,
      this.requisitionIsUpdatedEventFeatureService.requisitionUpdated$,
    )
      .pipe(
        switchMap(() => forkJoin([this.loadRequisitions(), this.loadAppointments()])),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.checkActiveAppointments(new Date());
        },
        error: () => {},
      });

    this.applicationRuntimeService.isStable$
      .pipe(
        first((isStable) => isStable),
        switchMap(() => everyMinute$({ fireInitial: true })),
        takeUntil(this.destroyed$),
      )
      .subscribe((date) => this.checkActiveAppointments(date));

    this.subscribeToRequisitionClosedEvent(() => this.onRequisitionClosed());
    this.subscribeToNurseQueueEvents((event) => this.handleNursingQueueUpdates(event));
  }

  destroy() {
    this.destroyed$.next();
  }

  loadRequisitions(): Observable<void> {
    return this.requisitionApiProviderService.getAll().pipe(
      catchError(() => {
        return of([]);
      }),
      tap((requisitions) => {
        this._requisitions$.next(requisitions);

        const currentRequisition = this._requisitions$.value?.[0];
        const socketProvider = currentRequisition?.socket_provider?.toUpperCase() as WebsocketAvailableProvider;
        if (currentRequisition && socketProvider) {
          this.websocketLifecycleService.setProvider(socketProvider);
        }
      }),
      map(() => null),
    );
  }

  loadAppointments(): Observable<void> {
    const todayStaticDate = DateFormatter.dateToString(new Date(), { format: STATIC_DATE_FORMAT });
    return this.timeSlotsApiProviderService
      .getPatientAppointmentTimeSlots(todayStaticDate, ERequisitionFilterType.Active)
      .pipe(
        catchError(() => {
          this.notificationsService.error({
            message: TranslateService.localize('errors.unable-load-user-data'),
          });
          return of([]);
        }),
        tap((appointments) => this._appointments$.next(appointments)),
        map(() => null),
      );
  }

  private closeRequisition(requisitionId: TGuid) {
    this.setActionInProgress(EPatientSessionAction.CloseRequisition);

    this.requisitionApiProviderService
      .close(requisitionId)
      .pipe(finalize(() => this.clearActionInProgress()))
      .subscribe({
        error: (err) => {
          this.notificationsService.error({
            message: err?.error?.message || TranslateService.localize('nouns.error'),
          });
        },
      });

    this.modalRef = this.modalOverlayService.openOverlay<ReasonsComponent>(ReasonsComponent);
    this.modalRef.instance.headerTitle = TranslateService.localize('modals.reasons.title');

    this.modalRef.instance.submitReason$
      .pipe(takeUntil(race(this.destroyed$.asObservable(), this.modalRef.instance.close$)))
      .subscribe((reason: IRequisitionReason) => {
        reason.requisition_id = requisitionId;

        this.modalReasonsService.sendReason(reason).subscribe({
          complete: () => {
            this.modalRef.close();
          },
        });
      });
  }

  private checkActiveAppointments(currentDate: Date) {
    const appointments = this._appointments$.value;
    const shouldBeActiveAppointments = appointments.filter((a) => currentDate >= a.start);
    const newActiveAppointmentsIds: TGuid[] = shouldBeActiveAppointments.map((app) => app.id);
    const currentActiveAppointmentsIds: TGuid[] = this._appointmentsLiveIds$.value;

    if (!isArraysContentEqual(newActiveAppointmentsIds, currentActiveAppointmentsIds)) {
      this.broadcastEventsService.broadcast(new CloseAsidePageEvent());
      this._appointmentsLiveIds$.next(newActiveAppointmentsIds);
    }
  }

  getPatientSchedules(PatientId: string): Observable<Schedule[]> {
    return this.apiService.get<Schedule[]>(
      `${environment.environmentVariables.apiCoreUrl}/ExternalAppointment/Card/${PatientId}`,
    );
  }

  public createRequisition(
    questionnaireId: TGuid,
    answers: IRequisitionAnswerDTO[],
    doctorSpecialization: number,
    doctorProfileId?: string,
    requisitionConfirmationKey: string = null,
    requisitionRegulationCode: string = null,
    isRequestPresential?: boolean,
    isViolenceVictim: boolean | null = null,
    requestedProfileTagId?: string,
    externalAppointmentId?: string | null,
    isIncarcerated: boolean | null = null,
  ): Observable<any> {
    const request$ = this.requisitionApiProviderService.createForLiveQueue(
      questionnaireId,
      answers,
      isRequestPresential,
      requestedProfileTagId,
      doctorSpecialization,
      doctorProfileId,
      requisitionConfirmationKey,
      requisitionRegulationCode,
      isViolenceVictim,
      externalAppointmentId,
      undefined,
      isIncarcerated,
    );

    return request$.pipe(
      catchError((error) => {
        this.notificationsService.error({
          message: error?.error || TranslateService.localize('questionnaire.send.error.message'),
        });
        return throwError(error);
      }),
    );
  }

  public refreshActiveRequisitions(): void {
    forkJoin([this.loadRequisitions(), this.loadAppointments()]).subscribe({
      next: () => {
        this.checkActiveAppointments(new Date());
      },
      error: (err) => {
        console.error('Erro ao atualizar requisições ativas', err);
      },
    });
  }

  public closeRequisitionWithoutModal(requisitionId: TGuid): Observable<void> {
    this.setActionInProgress(EPatientSessionAction.CloseRequisition);

    return this.requisitionApiProviderService.close(requisitionId).pipe(
      finalize(() => this.clearActionInProgress()),
      catchError((err) => {
        this.notificationsService.error({
          message: err?.error?.message || TranslateService.localize('nouns.error'),
        });
        return throwError(err);
      }),
      map(() => null),
    );
  }

  public getDoctorSpecializationOptions(role: string, client: string): Observable<DoctorSpecialization[]> {
    const url = `${environment.environmentVariables.apiCoreUrl}/Requisition/Impersonate/Specialization?role=${role}&client=${client}`;
    return this.apiService.get(url);
  }

  public getAvailableSpecialtiesForMedicalReferral(): Observable<ReferralGuideSpecialty[]> {
    const url = `${environment.environmentVariables.apiCoreUrl}/Specialty`;
    return this.apiService.get<ReferralGuideSpecialty[]>(url);
  }

  private subscribeToRequisitionClosedEvent(cb: () => void) {
    this.requisitionIsUpdatedEventFeatureService.requisitionClosed$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => cb());
  }

  private subscribeToNurseQueueEvents(cb: (event: IRequisitionUpdateEvent) => void) {
    this.requisitionIsUpdatedEventFeatureService.requisitionUpdated$
      .pipe(
        takeUntil(this.destroyed$),
        filter((event) => event.activeStage?.type === ERequisitionStageTypeDTO.NurseQueue),
      )
      .subscribe((e) => cb(e));
  }

  private hasDoctorInRequisition(doctors: IDoctor[]): boolean {
    return Array.isArray(doctors) && doctors.length > 0;
  }

  private openDoctorRatingModal() {
    this.doctorRatingModalRef = this.modalOverlayService.openOverlay(ModalDoctorRatingComponent);
  }

  private handlePostDoctorRating(doctorRating: DoctorRatingDTO) {
    this.doctorRatingModalRef.instance.isLoading = true;

    return this.ratingService.postDoctorRating(doctorRating).subscribe(
      () => {
        const successMessage = TranslateService.localize('modals.rating.acknowledgment');
        this.notificationsService.success({ message: successMessage });
        this.doctorRatingModalRef.instance.isLoading = false;
        this.doctorRatingModalRef.close();
      },
      () => {
        const errorMessage = TranslateService.localize('modals.rating.error');
        this.notificationsService.error({ message: errorMessage });
      },
    );
  }

  private async handleNursingQueueUpdates(event: IRequisitionUpdateEvent) {
    switch (event.activeStage?.status) {
      case ERequisitionStageStatusDTO.NursingCompleted:
        await this.videoCallManagerService.endCurrentCall(true, false);
        break;
    }
  }

  private async onRequisitionClosed() {
    await this.videoCallManagerService.endCurrentCall(true, false);

    const closedRequisition = this._requisitions$.value[0];
    const { doctors } = closedRequisition;

    const shouldOpenDoctorRatingModal = this.hasDoctorInRequisition(doctors);
    if (!shouldOpenDoctorRatingModal) return;

    const requisitionId = closedRequisition.id;
    const requisitionDoctorProfileId = closedRequisition.doctors[0].id;

    this.openDoctorRatingModal();

    this.doctorRatingModalRef.instance.submit$
      .pipe(takeUntil(race(this.destroyed$.asObservable(), this.doctorRatingModalRef.instance.close$)))
      .subscribe((doctorRating) => {
        this.handlePostDoctorRating({
          doctor_profile_id: requisitionDoctorProfileId,
          requisition_id: requisitionId,
          rating: doctorRating.rating,
          review: doctorRating.review,
          is_anonymous: doctorRating.is_anonymous,
        });
      });
  }
}
